import { useParams } from 'react-router-dom';
import type { SpaceMembership } from '../contexts/SpaceContext';
import { useSpace } from './useSpace';

export type Permissions = {
  role: SpaceMembership['role'] | null;
  isSuperAdmin: boolean;
  /** Can perform any write action in the current space (mirrors RLS `has_space_role(space_id, 'admin')`). */
  isAdmin: boolean;
  /** Admins and council members read private notes; only admins edit them. */
  canSeePrivateNotes: boolean;
  /** The inbox is the admins' working tool: hidden from council members and readers (also enforced by RLS). */
  canSeeInbox: boolean;
  /** The AI assistant is for admins and council members, never readers (also enforced by ai-chat and RLS). */
  canUseAssistant: boolean;
  isLoading: boolean;
};

/**
 * Single source of truth for the current user's rights in the current space.
 * Reads the slug from the URL first so route guards never see a stale space.
 */
export function usePermissions(): Permissions {
  const { slug } = useParams<{ slug: string }>();
  const { currentSpace, memberships, isSuperAdmin, isLoading } = useSpace();

  const spaceSlug = slug ?? currentSpace?.slug;
  const membershipRole = memberships.find((m) => m.space.slug === spaceSlug)?.role ?? null;
  const role = isSuperAdmin ? 'admin' : membershipRole;
  const isAdmin = role === 'admin';
  // Council-only areas: hidden from readers (co-owners)
  const isCouncil = isAdmin || role === 'board_member';

  return {
    role,
    isSuperAdmin,
    isAdmin,
    canSeePrivateNotes: isCouncil,
    canSeeInbox: isAdmin,
    canUseAssistant: isCouncil,
    isLoading,
  };
}
